/* Open-High-Low-Close (OHLC) chart for Compare Portfolios. Candlesticks occlude
   each other when overlaid, so we chart ONE focused portfolio's equity as candles
   (with a focus selector to switch) and overlay the S&P 500 plus every OTHER
   compared portfolio as thin return lines, so all of them can be compared on one
   axis. A $/% toggle flips between dollar value and return-since-window-start; the
   1W/1M/ALL toggle re-windows AND re-buckets the candles client-side (1W → daily,
   1M/ALL → weekly). Optional EMA(5) overlays for the focus + SPY. Hover for OHLC +
   period/total detail, or drag across the chart to compare two points. Theme-aware:
   the canvas can't read CSS vars, so colors are resolved from the active NQ theme
   tokens and re-applied on theme change.

   Lives in SharedComponents (imported by both the app and ledger compare screens) so
   the two stay in lockstep — it previously drifted as two diverged copies. */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  createChart,
  CandlestickSeries,
  LineSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  type IChartApi,
  type ISeriesApi,
  type CandlestickData,
  type LineData,
  type Time,
} from 'lightweight-charts';
import type { PerformanceHistoryPoint } from '../types';
import {
  BENCHMARK_LABEL,
  type ComparisonAccount,
  type PerformanceHistoryRange,
} from './comparisonTypes';
import { buildOHLCCandlesticks } from './comparisonUtils';
import { isComparisonHistoryPending } from './comparisonLoading';
import ComparisonChartSkeleton from './ComparisonChartSkeleton';
import { formatCurrency, formatSignedCurrency } from '../SharedAccountBody';

const CHART_HEIGHT = 340;
// Default toggle set. Callers that fetch intraday data (e.g. the app's compare
// page) can pass `rangeOptions={['1D','1W','1M','ALL']}` to expose the 1D view;
// surfaces that only fetch the daily ALL series keep the 3-option default.
const DEFAULT_RANGE_OPTIONS: PerformanceHistoryRange[] = ['1W', '1M', 'ALL'];
const EMA_PERIOD = 5;
// Windows for the range toggle (seconds). ALL = no windowing. 1D is fetched as
// its own intraday window (no further client windowing needed).
const RANGE_WINDOW_SECONDS: Record<PerformanceHistoryRange, number> = {
  '1D': Number.POSITIVE_INFINITY,
  '1W': 7 * 24 * 60 * 60,
  '1M': 31 * 24 * 60 * 60,
  ALL: Number.POSITIVE_INFINITY,
};

type Mode = '$' | '%';
type Bar = { time: Time; close: number; bench?: number; x: number; y: number };

// NQ-themed chart chrome, mirroring ComparisonReturnChart so the two views match.
const CARD_CLASS =
  'rounded-[12px] border border-white/[0.08] bg-white/[0.025] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';
const GROUP_CLASS =
  'inline-flex gap-1 rounded-[8px] border border-white/[0.08] bg-white/[0.04] p-[3px]';
const segBtnClass = (active: boolean) =>
  `cursor-pointer rounded-[6px] border-none bg-transparent px-2.5 py-1 text-[0.78rem] font-semibold tracking-[0.04em] ${active ? 'bg-white/10 text-white/95' : 'text-white/60'}`;
const CHART_EMPTY = 'grid h-[200px] place-items-center text-[0.9rem] text-white/55';
const TOOLTIP_CLASS =
  'pointer-events-none absolute z-20 min-w-[240px] max-w-[360px] rounded-[8px] border border-white/[0.14] bg-[rgba(13,17,30,0.98)] px-3 py-2 text-[13px] shadow-[0_8px_24px_rgba(0,0,0,0.45)]';

// Static canvas chrome (theme-independent dark surface). These live in the chart's
// JS options, not JSX style, so the tokens-only lint guard does not apply.
const TV_CHROME = {
  textColor: 'rgba(255,255,255,0.5)',
  vertGrid: 'rgba(255,255,255,0.04)',
  horzGrid: 'rgba(255,255,255,0.05)',
  border: 'rgba(255,255,255,0.08)',
  fontFamily: 'Roboto, system-ui, sans-serif',
};
const DOLLAR_PRICE_FORMAT = { type: 'price' as const, precision: 0, minMove: 1 };
const PCT_PRICE_FORMAT = {
  type: 'custom' as const,
  minMove: 0.01,
  formatter: (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(1)}%`,
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
// Candles use 'YYYY-MM-DD' string times; normalize to a "Mon DD" label for tooltips.
const fmtTvDate = (t: Time): string => {
  if (typeof t === 'string') {
    const [, m, d] = t.split('-').map(Number);
    return `${MONTHS[(m || 1) - 1]} ${String(d).padStart(2, '0')}`;
  }
  if (typeof t === 'object' && 'month' in t) {
    return `${MONTHS[(t.month || 1) - 1]} ${String(t.day).padStart(2, '0')}`;
  }
  return String(t);
};

// PerformanceCandle/point timestamps are Unix seconds (UTC); bucket to a calendar
// day so candles and the benchmark line align on the same time axis.
const toDayString = (timestampSeconds: number): string =>
  new Date(timestampSeconds * 1000).toISOString().slice(0, 10);

const cssVar = (name: string, fallback: string): string => {
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
};
const themeColors = () => ({
  up: cssVar('--nq-up', '#2ee66b'),
  down: cssVar('--nq-down', '#ff4d5e'),
  bench: cssVar('--nq-gold', '#f7941d'),
});

const asPct = (v: number, base: number) => (v / base - 1) * 100;
const toPctCandles = (candles: CandlestickData[], base: number): CandlestickData[] =>
  base > 0
    ? candles.map((c) => ({
        time: c.time,
        open: asPct(c.open, base),
        high: asPct(c.high, base),
        low: asPct(c.low, base),
        close: asPct(c.close, base),
      }))
    : candles;
const toPctLine = (line: LineData[], base: number): LineData[] =>
  base > 0 ? line.map((p) => ({ time: p.time, value: asPct(p.value, base) })) : line;

// Exponential moving average over a value series, aligned 1:1 with the input times.
const emaLine = (line: LineData[], period: number): LineData[] => {
  if (line.length === 0) return [];
  const k = 2 / (period + 1);
  let prev = line[0]!.value;
  return line.map((p, i) => {
    prev = i === 0 ? p.value : p.value * k + prev * (1 - k);
    return { time: p.time, value: prev };
  });
};

const toneClass = (v: number) => (v >= 0 ? 'text-positive' : 'text-negative');
const fmtPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
const fmtPts = (v: number) => `${v >= 0 ? '+' : '−'}${Math.abs(v).toFixed(2)} pts`;

type RawCandle = { open: number; high: number; low: number; close: number };

interface AccountSeries {
  candles: CandlestickData[];
  bench: LineData[];
  baseClose: number;
  baseBench: number;
  sinceStartPct: number;
  /** Raw ($) OHLC per candle time, for the tooltip. */
  rawByTime: Map<string, RawCandle>;
  /** This-bucket (period) % return for portfolio + benchmark, per candle time. */
  periodByTime: Map<string, { port: number; bench?: number }>;
  /** % return since window start, per candle time (for the other-portfolio lines). */
  returnLine: LineData[];
}

// Window points to the selected range, then bucket to OHLC (1W → daily, else weekly).
const buildAccountSeries = (
  account: ComparisonAccount,
  range: PerformanceHistoryRange,
): AccountSeries => {
  const all = [...(account.history?.points ?? [])].sort((a, b) => a.timestamp - b.timestamp);
  const lastTs = all.length ? all[all.length - 1]!.timestamp : 0;
  const window = RANGE_WINDOW_SECONDS[range];
  const points: PerformanceHistoryPoint[] = Number.isFinite(window)
    ? all.filter((p) => p.timestamp >= lastTs - window)
    : all;

  const rawCandles = points.length ? buildOHLCCandlesticks(points, range) : [];
  const candles: CandlestickData[] = [];
  const rawByTime = new Map<string, RawCandle>();
  for (const c of rawCandles) {
    const time = toDayString(c.timestamp);
    candles.push({ time: time as Time, open: c.open, high: c.high, low: c.low, close: c.close });
    rawByTime.set(time, { open: c.open, high: c.high, low: c.low, close: c.close });
  }

  const benchByDay = new Map<string, number>();
  if (account.history?.benchmarkAvailable) {
    for (const p of points) benchByDay.set(toDayString(p.timestamp), p.benchmark);
  }
  const bench: LineData[] = [];
  for (const c of candles) {
    const value = benchByDay.get(c.time as string);
    if (value !== undefined) bench.push({ time: c.time, value });
  }

  const baseClose = rawCandles[0]?.open ?? 0;
  const baseBench = bench.length ? points[0]?.benchmark ?? 0 : 0;
  const lastClose = rawCandles[rawCandles.length - 1]?.close ?? 0;
  const sinceStartPct = baseClose > 0 ? ((lastClose - baseClose) / baseClose) * 100 : 0;

  // Per-bucket (period) return for portfolio + benchmark, and the % return line.
  const periodByTime = new Map<string, { port: number; bench?: number }>();
  const returnLine: LineData[] = [];
  let prevClose: number | undefined;
  let prevBench: number | undefined;
  const benchAt = new Map<string, number>(bench.map((b) => [b.time as string, b.value]));
  for (const c of candles) {
    const time = c.time as string;
    const close = rawByTime.get(time)!.close;
    const open = rawByTime.get(time)!.open;
    const port = prevClose && prevClose > 0 ? (close / prevClose - 1) * 100 : open > 0 ? (close / open - 1) * 100 : 0;
    const bv = benchAt.get(time);
    const benchPeriod = bv !== undefined && prevBench && prevBench > 0 ? (bv / prevBench - 1) * 100 : undefined;
    periodByTime.set(time, { port, bench: benchPeriod });
    if (baseClose > 0) returnLine.push({ time: c.time, value: asPct(close, baseClose) });
    prevClose = close;
    if (bv !== undefined) prevBench = bv;
  }

  return { candles, bench, baseClose, baseBench, sinceStartPct, rawByTime, periodByTime, returnLine };
};

interface Props {
  accounts: ComparisonAccount[];
  selectedRange?: PerformanceHistoryRange;
  onRangeChange?: (range: PerformanceHistoryRange) => void;
  /** Ranges shown in the toggle. Defaults to daily-only (1W/1M/ALL). */
  rangeOptions?: PerformanceHistoryRange[];
  loading?: boolean;
  error?: string | null;
}

const ComparisonCandlestickChart = ({
  accounts,
  selectedRange,
  onRangeChange,
  rangeOptions = DEFAULT_RANGE_OPTIONS,
  loading,
  error,
}: Props) => {
  const range = selectedRange ?? 'ALL';

  const seriesByAccount = useMemo(() => {
    const map = new Map<string, AccountSeries>();
    for (const acc of accounts) map.set(acc.accountId, buildAccountSeries(acc, range));
    return map;
  }, [accounts, range]);

  const leaderId = useMemo(() => {
    let best: ComparisonAccount | undefined;
    let bestPct = -Infinity;
    for (const acc of accounts) {
      const s = seriesByAccount.get(acc.accountId);
      if (s && s.candles.length > 0 && s.sinceStartPct > bestPct) {
        bestPct = s.sinceStartPct;
        best = acc;
      }
    }
    return (best ?? accounts[0])?.accountId ?? '';
  }, [accounts, seriesByAccount]);

  const [focusId, setFocusId] = useState(leaderId);
  const [mode, setMode] = useState<Mode>('%');
  const [showOthers, setShowOthers] = useState(true);
  const [showEma, setShowEma] = useState(false);
  const [hover, setHover] = useState<Bar | null>(null);
  const [sel, setSel] = useState<{ from: Bar; to: Bar } | null>(null);

  // Derive the focused account purely: honor the user's pick while it is still
  // present, else fall back to the leader.
  const effectiveId = accounts.some((a) => a.accountId === focusId) ? focusId : leaderId;
  const focus = accounts.find((a) => a.accountId === effectiveId);
  const focusSeries = focus ? seriesByAccount.get(focus.accountId) : undefined;
  const hasFocusData = Boolean(focusSeries && focusSeries.candles.length > 0);
  // Other-portfolio return lines only share a scale with the candles in % mode.
  const othersVisible = showOthers && mode === '%';

  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const candleRef = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const lineRef = useRef<ISeriesApi<'Line'> | null>(null);
  // Extra line series (EMA + other portfolios), reconciled on each data update.
  const extraRef = useRef<ISeriesApi<'Line'>[]>([]);
  const hoveredRef = useRef<Bar | null>(null);
  const draggingRef = useRef(false);
  // Raw ($) base for the current window, so tooltips read correctly in $ mode.
  const startRef = useRef<{ close: number; bench: number }>({ close: 0, bench: 0 });

  const historyPending = Boolean(loading) || isComparisonHistoryPending(accounts);

  // Create chart + the focus candle series + SPY line once (after container exists).
  useEffect(() => {
    if (historyPending) return;
    const container = containerRef.current;
    if (!container) return;

    const chart = createChart(container, {
      width: container.clientWidth,
      height: CHART_HEIGHT,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: TV_CHROME.textColor,
        fontFamily: TV_CHROME.fontFamily,
        attributionLogo: false,
      },
      grid: { vertLines: { color: TV_CHROME.vertGrid }, horzLines: { color: TV_CHROME.horzGrid } },
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderColor: TV_CHROME.border },
      timeScale: { borderColor: TV_CHROME.border, fixLeftEdge: true, fixRightEdge: true },
      // Disable pan/zoom so a mouse drag selects a window for drag-to-compare.
      handleScroll: { mouseWheel: false, pressedMouseMove: false, horzTouchDrag: false, vertTouchDrag: false },
      handleScale: { axisPressedMouseMove: false, mouseWheel: false, pinch: false },
    });

    const c = themeColors();
    const candle = chart.addSeries(CandlestickSeries, {
      upColor: c.up,
      downColor: c.down,
      borderUpColor: c.up,
      borderDownColor: c.down,
      wickUpColor: c.up,
      wickDownColor: c.down,
      priceFormat: DOLLAR_PRICE_FORMAT,
    });
    const line = chart.addSeries(LineSeries, {
      color: c.bench,
      lineWidth: 2,
      lineStyle: LineStyle.Dashed,
      priceLineVisible: false,
      lastValueVisible: false,
      // The crosshair dot on the benchmark read as "the subject"; the candle is the
      // subject, so suppress the SPY marker (the crosshair line still tracks it).
      crosshairMarkerVisible: false,
    });

    chartRef.current = chart;
    candleRef.current = candle;
    lineRef.current = line;

    chart.subscribeCrosshairMove((param) => {
      const cd = param.seriesData.get(candle) as CandlestickData | undefined;
      const ld = param.seriesData.get(line) as LineData | undefined;
      if (!param.time || !param.point || !cd || cd.close === undefined) {
        if (!draggingRef.current) {
          hoveredRef.current = null;
          setHover(null);
        }
        return;
      }
      const bar: Bar = { time: param.time, close: cd.close, bench: ld?.value, x: param.point.x, y: param.point.y };
      hoveredRef.current = bar;
      if (draggingRef.current) {
        setSel((s) => (s ? { from: s.from, to: bar } : s));
      } else {
        setHover(bar);
      }
    });

    // Drag-to-compare: mousedown anchors, drag extends, mouseup finalizes (a
    // zero-width drag is treated as a click and clears any selection).
    const onDown = () => {
      const h = hoveredRef.current;
      if (!h) return;
      draggingRef.current = true;
      setHover(null);
      setSel({ from: h, to: h });
    };
    const onUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      setSel((s) => (s && s.from.time === s.to.time ? null : s));
    };
    container.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);

    const ro = new ResizeObserver((entries) => {
      const w = Math.floor(entries[0].contentRect.width);
      if (w > 0) chart.applyOptions({ width: w });
    });
    ro.observe(container);

    const mo = new MutationObserver(() => {
      const t = themeColors();
      candle.applyOptions({
        upColor: t.up,
        downColor: t.down,
        borderUpColor: t.up,
        borderDownColor: t.down,
        wickUpColor: t.up,
        wickDownColor: t.down,
      });
      line.applyOptions({ color: t.bench });
    });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class', 'data-theme'] });

    return () => {
      container.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      ro.disconnect();
      mo.disconnect();
      chart.remove();
      chartRef.current = null;
      candleRef.current = null;
      lineRef.current = null;
      extraRef.current = [];
    };
  }, [historyPending]);

  // Update data on focus/range/mode/overlay change; reset interaction state.
  useEffect(() => {
    const candle = candleRef.current;
    const line = lineRef.current;
    const chart = chartRef.current;
    if (!candle || !line || !chart) return;

    // Clear previously-added extra series (EMA + other portfolios).
    for (const s of extraRef.current) chart.removeSeries(s);
    extraRef.current = [];

    if (!focusSeries || focusSeries.candles.length === 0) {
      candle.setData([]);
      line.setData([]);
      setSel(null);
      setHover(null);
      return;
    }

    const { candles, bench, baseClose, baseBench } = focusSeries;
    candle.applyOptions({ priceFormat: mode === '%' ? PCT_PRICE_FORMAT : DOLLAR_PRICE_FORMAT });
    const displayCandles = mode === '%' ? toPctCandles(candles, baseClose) : candles;
    const displayBench = mode === '%' ? toPctLine(bench, baseBench) : bench;
    candle.setData(displayCandles);
    line.setData(displayBench);

    const c = themeColors();
    // Other portfolios as thin return lines (only in % mode — shared scale).
    if (othersVisible) {
      for (const acc of accounts) {
        if (acc.accountId === focus?.accountId) continue;
        const s = seriesByAccount.get(acc.accountId);
        if (!s || s.returnLine.length === 0) continue;
        const series = chart.addSeries(LineSeries, {
          color: acc.color,
          lineWidth: 2,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        series.setData(s.returnLine);
        extraRef.current.push(series);
      }
    }

    // EMA(5) overlays for the focus close + SPY.
    if (showEma) {
      const focusEma = chart.addSeries(LineSeries, {
        color: c.up,
        lineWidth: 1,
        lineStyle: LineStyle.Dotted,
        priceLineVisible: false,
        lastValueVisible: false,
        crosshairMarkerVisible: false,
      });
      focusEma.setData(emaLine(displayCandles.map((d) => ({ time: d.time, value: d.close })), EMA_PERIOD));
      extraRef.current.push(focusEma);
      if (displayBench.length) {
        const benchEma = chart.addSeries(LineSeries, {
          color: c.bench,
          lineWidth: 1,
          lineStyle: LineStyle.Dotted,
          priceLineVisible: false,
          lastValueVisible: false,
          crosshairMarkerVisible: false,
        });
        benchEma.setData(emaLine(displayBench, EMA_PERIOD));
        extraRef.current.push(benchEma);
      }
    }

    chart.timeScale().fitContent();
    startRef.current = { close: baseClose, bench: baseBench };
    setSel(null);
    setHover(null);
  }, [focusSeries, mode, othersVisible, showEma, accounts, seriesByAccount, focus?.accountId]);

  const clientW = containerRef.current?.clientWidth ?? 800;
  const clampLeft = (x: number) => Math.min(Math.max(x, 8), clientW - 372);
  const showCompare = sel && sel.from.time !== sel.to.time;

  // Hover metrics. In % mode the displayed close IS the return since window start;
  // in $ mode we derive it from the raw dollar base.
  const base = startRef.current;
  const hoverTime = hover ? (hover.time as string) : undefined;
  const rawCandle = hoverTime ? focusSeries?.rawByTime.get(hoverTime) : undefined;
  const periodHere = hoverTime ? focusSeries?.periodByTime.get(hoverTime) : undefined;
  const hTotalPct = !hover ? 0 : mode === '%' ? hover.close : base.close > 0 ? ((hover.close - base.close) / base.close) * 100 : 0;
  const hBenchTotalPct =
    !hover || hover.bench === undefined
      ? undefined
      : mode === '%'
        ? hover.bench
        : base.bench > 0
          ? ((hover.bench - base.bench) / base.bench) * 100
          : undefined;
  const hAlpha = hBenchTotalPct !== undefined ? hTotalPct - hBenchTotalPct : undefined;
  // OHLC readout in the active mode's units.
  const fmtVal = (v: number) => (mode === '$' ? formatCurrency(v) : fmtPct(asPct(v, base.close)));

  // Compare metrics between the two selected bars (ordered by time).
  let cmp: null | {
    fromLabel: string;
    toLabel: string;
    pAmt?: number;
    pPct: number;
    bPct?: number;
    alpha?: number;
    left: number;
    width: number;
    tipX: number;
  } = null;
  if (showCompare && sel) {
    const a = sel.from.time <= sel.to.time ? sel.from : sel.to;
    const b = sel.from.time <= sel.to.time ? sel.to : sel.from;
    const hasBench = a.bench !== undefined && b.bench !== undefined;
    // % mode: values are cumulative returns → window change is the pts difference.
    // $ mode: values are dollars → window change is a $ amount + relative %.
    const pPct = mode === '%' ? b.close - a.close : a.close > 0 ? ((b.close - a.close) / a.close) * 100 : 0;
    const bPct = !hasBench
      ? undefined
      : mode === '%'
        ? b.bench! - a.bench!
        : a.bench! > 0
          ? ((b.bench! - a.bench!) / a.bench!) * 100
          : undefined;
    cmp = {
      fromLabel: fmtTvDate(a.time),
      toLabel: fmtTvDate(b.time),
      pAmt: mode === '$' ? b.close - a.close : undefined,
      pPct,
      bPct,
      alpha: bPct !== undefined ? pPct - bPct : undefined,
      left: Math.min(a.x, b.x),
      width: Math.abs(b.x - a.x),
      tipX: sel.to.x,
    };
  }
  const fmtChange = (v: number) => (mode === '%' ? fmtPts(v) : fmtPct(v));

  if (historyPending) {
    return <ComparisonChartSkeleton title="Open-High-Low-Close (OHLC) Chart" height={CHART_HEIGHT} />;
  }

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-row flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="m-0 text-[1.05rem] font-semibold text-foreground">Open-High-Low-Close (OHLC) Chart</h3>
          <p className="mt-1 text-[0.85rem] text-muted-foreground">
            One focused portfolio&apos;s value as OHLC candles — each candle is the open, high, low
            and close over a day (1W) or week (1M/ALL) — with the S&amp;P 500 and the other compared
            portfolios overlaid as lines. Hover for detail or drag across two points to compare.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className={GROUP_CLASS} role="group" aria-label="Value mode">
            {(['$', '%'] as Mode[]).map((m) => (
              <button key={m} type="button" className={segBtnClass(m === mode)} onClick={() => setMode(m)} aria-pressed={m === mode}>
                {m}
              </button>
            ))}
          </div>
          {onRangeChange && (
            <div className={GROUP_CLASS} role="group" aria-label="Time range">
              {rangeOptions.map((r) => (
                <button key={r} type="button" className={segBtnClass(range === r)} onClick={() => onRangeChange(r)} aria-pressed={range === r}>
                  {r}
                </button>
              ))}
            </div>
          )}
          <div className={GROUP_CLASS} role="group" aria-label="Overlays">
            <button
              type="button"
              className={segBtnClass(showOthers)}
              onClick={() => setShowOthers((v) => !v)}
              aria-pressed={showOthers}
              disabled={mode !== '%'}
              title={mode !== '%' ? 'Other portfolios overlay in % mode' : 'Overlay the other compared portfolios'}
              style={mode !== '%' ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            >
              Others
            </button>
            <button type="button" className={segBtnClass(showEma)} onClick={() => setShowEma((v) => !v)} aria-pressed={showEma} title="Overlay EMA(5) for the focus + SPY">
              EMA 5
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-2 rounded-[8px] border border-negative/25 bg-negative/8 px-2.5 py-2 text-[0.85rem] text-negative">
          {error}
        </div>
      )}

      {/* Focus selector across the compared accounts. */}
      <div className="mb-3 flex flex-wrap gap-1.5">
        {accounts.map((acc) => {
          const active = acc.accountId === focus?.accountId;
          return (
            <button
              key={acc.accountId}
              type="button"
              onClick={() => setFocusId(acc.accountId)}
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-[6px] border px-2.5 py-1 text-[0.78rem] transition-colors ${
                active ? 'bg-white/10 text-white/95' : 'border-white/[0.08] bg-transparent text-white/60 hover:text-white/95'
              }`}
              style={active ? { borderColor: acc.color } : undefined}
              aria-pressed={active}
              title={active ? `${acc.label} — shown as candles` : `Focus ${acc.label} as candles`}
            >
              <span className="size-2 flex-none rounded-full" style={{ backgroundColor: acc.color }} aria-hidden />
              {acc.label}
            </button>
          );
        })}
      </div>

      <div className="relative select-none">
        {/* selection band */}
        {cmp && (
          <div
            className="pointer-events-none absolute top-0 bottom-0 z-10 border-x border-primary/60 bg-primary/10"
            style={{ left: cmp.left, width: cmp.width }}
          />
        )}

        {/* compare tooltip — the individual change between the two selected points */}
        {focus && cmp && (
          <div className={TOOLTIP_CLASS} style={{ left: clampLeft(cmp.tipX + 14), top: 8 }}>
            <p className="m-0 mb-1.5 text-[12px] text-white/55">
              {cmp.fromLabel} <span className="text-foreground">→</span> {cmp.toLabel}
            </p>
            <TvRow
              label={focus.label}
              labelColor={focus.color}
              value={cmp.pAmt !== undefined ? formatSignedCurrency(cmp.pAmt) : undefined}
              right={fmtChange(cmp.pPct)}
              rightClass={toneClass(cmp.pPct)}
              strong
            />
            <TvRow
              label={BENCHMARK_LABEL}
              right={cmp.bPct !== undefined ? fmtChange(cmp.bPct) : '—'}
              rightClass={cmp.bPct !== undefined ? toneClass(cmp.bPct) : undefined}
            />
            {cmp.alpha !== undefined && (
              <TvRow label="Alpha" right={fmtPts(cmp.alpha)} rightClass={toneClass(cmp.alpha)} bordered strong />
            )}
          </div>
        )}

        {/* hover tooltip — OHLC + this period's change and the cumulative total */}
        {focus && !cmp && hover && (
          <div className={TOOLTIP_CLASS} style={{ left: clampLeft(hover.x + 14), top: Math.max(hover.y - 8, 8) }}>
            <p className="m-0 mb-1.5 text-[12px] text-white/55">{fmtTvDate(hover.time)}</p>
            {rawCandle && (
              <div className="mb-1.5 grid grid-cols-2 gap-x-3 gap-y-0.5 border-b border-white/[0.12] pb-1.5 text-[12px] text-white/70 tabular-nums">
                <span>O <span className="text-foreground">{fmtVal(rawCandle.open)}</span></span>
                <span>H <span className="text-foreground">{fmtVal(rawCandle.high)}</span></span>
                <span>L <span className="text-foreground">{fmtVal(rawCandle.low)}</span></span>
                <span>C <span className="text-foreground">{fmtVal(rawCandle.close)}</span></span>
              </div>
            )}
            <div className="grid grid-cols-[minmax(0,1fr)_64px_64px] gap-3 pb-0.5 text-[11px] uppercase tracking-[0.04em] text-white/45">
              <span />
              <span className="text-right">Period</span>
              <span className="text-right">Total</span>
            </div>
            <TvRow
              label={focus.label}
              labelColor={focus.color}
              mid={periodHere ? fmtPct(periodHere.port) : '—'}
              midClass={periodHere ? toneClass(periodHere.port) : undefined}
              right={fmtPct(hTotalPct)}
              rightClass={toneClass(hTotalPct)}
              strong
            />
            <TvRow
              label={BENCHMARK_LABEL}
              mid={periodHere?.bench !== undefined ? fmtPct(periodHere.bench) : '—'}
              midClass={periodHere?.bench !== undefined ? toneClass(periodHere.bench) : undefined}
              right={hBenchTotalPct !== undefined ? fmtPct(hBenchTotalPct) : '—'}
              rightClass={hBenchTotalPct !== undefined ? toneClass(hBenchTotalPct) : undefined}
            />
            {hAlpha !== undefined && (
              <TvRow label={`Alpha vs ${BENCHMARK_LABEL}`} right={fmtPts(hAlpha)} rightClass={toneClass(hAlpha)} bordered strong />
            )}
          </div>
        )}

        {/* Empty state overlays the chart rather than unmounting it, so the single
            chart instance survives an empty→non-empty focus change. */}
        {!hasFocusData && (
          <div className={`${CHART_EMPTY} pointer-events-none absolute inset-0 z-10`}>
            {focus ? 'No candle data available for this portfolio.' : 'Select a portfolio to chart its value as candles.'}
          </div>
        )}

        <div ref={containerRef} className="h-[340px] w-full" />
      </div>
    </div>
  );
};

/* One tooltip row: label · optional value · optional mid (period) · right (total/change). */
interface TvRowProps {
  label: string;
  labelColor?: string;
  value?: string;
  mid?: string;
  midClass?: string;
  right?: string;
  rightClass?: string;
  strong?: boolean;
  bordered?: boolean;
}
const TvRow = ({ label, labelColor, value, mid, midClass, right, rightClass, strong, bordered }: TvRowProps) => (
  <div
    className={`grid ${mid !== undefined ? 'grid-cols-[minmax(0,1fr)_64px_64px]' : 'grid-cols-[minmax(0,1fr)_auto_72px]'} items-center gap-3 whitespace-nowrap py-0.5 ${
      strong ? 'font-semibold' : ''
    } ${bordered ? 'mt-1.5 border-t border-white/[0.12] pt-1.5' : ''}`}
  >
    <span
      className={`inline-flex min-w-0 items-center gap-1.5 truncate ${labelColor ? '' : 'text-foreground'}`}
      style={labelColor ? { color: labelColor } : undefined}
    >
      {labelColor && <span className="size-2 flex-none rounded-full" style={{ backgroundColor: labelColor }} aria-hidden />}
      {label}
    </span>
    {mid !== undefined ? (
      <span className={`text-right tabular-nums ${midClass ?? 'text-white/55'}`}>{mid}</span>
    ) : (
      <span className="text-right tabular-nums text-foreground">{value ?? ''}</span>
    )}
    <span className={`text-right tabular-nums ${rightClass ?? 'text-white/55'}`}>{right ?? '—'}</span>
  </div>
);

export default ComparisonCandlestickChart;
